import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Drawer, Empty, Input, List, message, Modal, Skeleton, Space, Switch, Tag, Typography } from "antd";
import {
  CheckCircleOutlined,
  CloseOutlined,
  EyeOutlined,
  EditOutlined,
  FileImageOutlined,
  FileProtectOutlined,
  FileTextOutlined,
  IdcardOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  StopOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import { useWorkspace } from "contexts/WorkspaceContext";
import SidePromotionSection from "components/promotions/SidePromotionSection";
import paths from "routes/paths";
import { olreadiaService } from "services/olreadiaService";
import { hasRichText, richTextMarkup, richTextPlainText } from "./richTextUtils";
import "./OlreadiaFlowPages.css";

const { Paragraph, Text, Title } = Typography;
const FILE_ID_PATTERN = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
const FILE_ID_EXACT_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isImageLike = (value = "") => /\.(png|jpe?g|webp|gif|heic|heif)(\?|#|$)/i.test(String(value || ""));
const MEDIA_PROPERTY_PATHS = new Set(["photos", "documents", "certifications", "licenses"]);
const JSON_PROPERTY_PATHS = new Set(["workExperienceHistory", "education", "references", ...MEDIA_PROPERTY_PATHS]);

const toList = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.content)) return value.content;
  if (Array.isArray(value?.items)) return value.items;
  return [];
};

const titleCase = (value) =>
  String(value || "")
    .replace(/[_-]/g, " ")
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const credentialType = (item) =>
  String(item?.credentialType || item?.type || item?.category || "").trim().toUpperCase();
const isLicenseCredential = (item) => credentialType(item) === "LICENSE";

const firstText = (...values) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
    if (typeof value === "number") return String(value);
  }
  return "";
};

const extractFileIds = (value) => {
  if (value == null) return [];
  if (Array.isArray(value)) return value.flatMap(extractFileIds);
  if (typeof value === "object") {
    const direct = firstText(value.fileId, value.id, value.uuid, value.documentId, value.photoId);
    return direct ? extractFileIds(direct) : [];
  }
  if (isUrlValue(value)) return [];
  return Array.from(new Set(String(value).match(FILE_ID_PATTERN) || []));
};

const fileTitle = (value, fallback) => {
  if (!value || typeof value !== "object") return fallback;
  return firstText(
    value.caption,
    value.name,
    value.fileName,
    value.filename,
    value.documentName,
    value.title,
    value.issuer,
  ) || fallback;
};

const isFileIdLike = (value) => FILE_ID_EXACT_PATTERN.test(String(value || "").trim());

const fileUrlFromValue = (value) => {
  if (typeof value === "string") return isUrlValue(value) ? value.trim() : "";
  if (!value || typeof value !== "object") return "";
  return (
    [
      value.signedUrl,
      value.signedURL,
      value.downloadUrl,
      value.downloadURL,
      value.fileUrl,
      value.fileURL,
      value.publicUrl,
      value.publicURL,
      value.url,
      value.uri,
      value.localUri,
      value.fileLocalUri,
    ].find(isUrlValue) || ""
  );
};

const directFileRefs = (value, fallback) => {
  if (value == null) return [];
  if (Array.isArray(value)) {
    return value.flatMap((item, index) => directFileRefs(item, `${fallback} ${index + 1}`));
  }
  const fileIds = extractFileIds(value);
  const url = fileUrlFromValue(value);
  if (!fileIds.length && !url) return [];
  return (fileIds.length ? fileIds : [url]).map((fileId, index) => ({
    fileId,
    url,
    label: fileTitle(value, fileIds.length > 1 ? `${fallback} ${index + 1}` : fallback),
    image: Boolean(url && isImageLike(url)),
  }));
};

const cleanPreviewObject = (value) => {
  if (!value || typeof value !== "object") return value;
  return Object.fromEntries(
    Object.entries(value)
      .filter(([key, item]) => (
        !/url|uri|fileid|photoid|documentid|uuid|^id$/i.test(key) &&
        item != null &&
        item !== "" &&
        typeof item !== "object" &&
        !isUrlValue(item) &&
        !isFileIdLike(item)
      ))
      .map(([key, item]) => [titleCase(key), item]),
  );
};

const detailedObjectValue = (value) => {
  if (value == null || value === "") return "";
  if (typeof value !== "object") return isUrlValue(value) || isFileIdLike(value) ? "" : String(value);
  const cleaned = cleanPreviewObject(value);
  const parts = Object.entries(cleaned).map(([key, item]) => `${key}: ${item}`);
  return parts.join(" • ");
};

const detailedListValue = (value) => {
  const list = Array.isArray(value) ? value : toList(value);
  if (!list.length) return "Not provided";
  return list
    .map((item, index) => {
      const detail = detailedObjectValue(item);
      if (!detail) return `${index + 1}. Provided`;
      return `${index + 1}. ${detail}`;
    })
    .join("\n");
};

const firstList = (...values) => {
  for (const value of values) {
    const list = toList(value);
    if (list.length) return list;
  }
  return [];
};

const stripMediaUrls = (value) => {
  if (value == null || value === "") return null;
  if (Array.isArray(value)) return value.map(stripMediaUrls).filter(Boolean);
  if (typeof value === "string") {
    if (isUrlValue(value)) return null;
    return isFileIdLike(value) ? { fileId: value } : value;
  }
  if (typeof value !== "object") return value;

  const fileId = firstText(value.fileId, value.id, value.uuid, value.documentId, value.photoId);
  const next = {
    ...(fileId && !isUrlValue(fileId) ? { fileId } : {}),
  };

  [
    "caption",
    "name",
    "fileName",
    "filename",
    "documentName",
    "title",
    "issuer",
    "certificationNumber",
    "issueDate",
    "issuedAt",
    "expiryDate",
    "expiresAt",
  ].forEach((key) => {
    const item = value[key];
    if (item != null && item !== "" && !isUrlValue(item)) next[key] = item;
  });

  return Object.keys(next).length ? next : null;
};

const mediaDisplayValue = (value, label) => {
  const refs = directFileRefs(value, label);
  const list = toList(value);
  const fileSummary = refs.length ? `${refs.length} file${refs.length === 1 ? "" : "s"} attached` : "";
  if (fileSummary) return fileSummary;
  if (list.length) return `${list.length} file${list.length === 1 ? "" : "s"} attached`;
  return "Not provided";
};

const displayStoredMediaPayload = (value) => {
  if (!value || typeof value !== "string") return "";
  try {
    const parsed = JSON.parse(value);
    const list = Array.isArray(parsed) ? parsed : [parsed];
    if (!list.length) return "";
    return `${list.length} file${list.length === 1 ? "" : "s"} attached`;
  } catch {
    return "";
  }
};

const displayStoredJsonPayload = (value, propertyPath) => {
  if (!value || typeof value !== "string") return "";
  try {
    const parsed = JSON.parse(value);
    const list = Array.isArray(parsed) ? parsed : [parsed];
    if (!list.length) return "";
    if (MEDIA_PROPERTY_PATHS.has(propertyPath)) {
      return `${list.length} file${list.length === 1 ? "" : "s"} attached`;
    }
    return detailedListValue(list);
  } catch {
    return "";
  }
};

const structuredCurrentItem = (value) => {
  if (value == null || value === "") return { value: "Not provided" };
  if (typeof value === "string") return { value };
  const stripped = stripMediaUrls(value);
  return stripped == null || (Array.isArray(stripped) && !stripped.length) ? value : stripped;
};

const readableStructuredValue = (value) => {
  if (value == null || value === "") return "";
  if (typeof value !== "object") return String(value);
  if (Array.isArray(value)) return value.map((item, index) => `${index + 1}. ${readableStructuredValue(item) || "Provided"}`).join("\n");
  const parts = Object.entries(value)
    .filter(([, item]) => item != null && item !== "" && typeof item !== "object")
    .slice(0, 8)
    .map(([key, item]) => `${titleCase(key)}: ${String(item)}`);
  return parts.join(" • ");
};

const compactObject = (value) => {
  if (value == null) return "Not provided";
  if (typeof value !== "object") return String(value);

  const preferred = [
    "caption",
    "name",
    "fullName",
    "profileName",
    "title",
    "role",
    "organization",
    "company",
    "employer",
    "institution",
    "program",
    "educationLevel",
    "issuer",
    "certificationNumber",
    "email",
    "phone",
    "city",
    "state",
    "country",
  ];
  const parts = preferred
    .map((key) => value[key])
    .filter((item) => item != null && item !== "" && !isUrlValue(item))
    .map(String);

  if (parts.length) return parts.join(" • ");

  const fallback = Object.entries(value)
    .filter(([key, item]) => (
      !/url|uri|fileid|photoid|documentid|uuid|^id$/i.test(key) &&
      item != null &&
      item !== "" &&
      typeof item !== "object" &&
      !isUrlValue(item) &&
      !isFileIdLike(item)
    ))
    .slice(0, 4)
    .map(([key, item]) => `${titleCase(key)}: ${String(item)}`);

  return fallback.length ? fallback.join(" • ") : "Provided";
};

const compactValue = (value) => {
  if (value == null || value === "") return "Not provided";
  if (Array.isArray(value)) {
    if (!value.length) return "Not provided";
    return value.map(compactObject).join("\n");
  }
  if (typeof value === "object") return compactObject(value);
  return isUrlValue(value) ? "File attached" : String(value);
};

const identitySummary = (user) => {
  const name = [user?.firstName, user?.middleName, user?.lastName].filter(Boolean).join(" ") || user?.name || user?.fullName;
  return [name, user?.email, user?.phone || user?.phoneNumber].filter(Boolean).join("\n") || "Not provided";
};

const addressSummary = (user) => {
  const address = user?.address || user?.homeAddress || user?.location;
  if (address) return compactValue(address);
  return [user?.street, user?.city, user?.state, user?.postalCode, user?.country].filter(Boolean).join(", ") || "Not provided";
};

const isUrlValue = (value) => /^(https?:|file:|blob:|data:)/i.test(String(value || "").trim());

const verificationItemValue = (item) => {
  const mediaSummary = JSON_PROPERTY_PATHS.has(item?.propertyPath)
    ? displayStoredJsonPayload(firstText(item?.currentItemValue, item?.valueFound), item?.propertyPath)
    : "";
  return mediaSummary || firstText(item?.valueFound, item?.currentItemValue, item?.value, item?.status, item?.matchResult)
    || readableStructuredValue(item?.currentItem);
};

const verificationItemViewUrl = (item) =>
  [
    item?.valueFoundUrl,
    item?.currentItemUrl,
    item?.url,
    item?.fileUrl,
    item?.signedUrl,
    item?.downloadUrl,
  ].find(isUrlValue) || "";

const formatDateTime = (value) => {
  if (!value) return "Not set";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return value;
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const statusTone = (status) => {
  const normalized = String(status || "").toUpperCase();
  if (["COMPLETED", "VERIFIED", "APPROVED", "PASSED"].includes(normalized)) return "good";
  if (["CANCELLED", "CANCELED", "FAILED", "REJECTED"].includes(normalized)) return "bad";
  if (["REQUESTED", "PENDING"].includes(normalized)) return "warn";
  return "active";
};

const profileTitle = (profile) =>
  firstText(
    profile?.profileName,
    profile?.title,
    profile?.profileTitle,
    profile?.professionRoleTitle,
    profile?.professionRole?.title,
    profile?.name,
  ) || "Professional profile";

const profileDetailItems = (profile) => [
  {
    label: "Line",
    value: firstText(
      profile?.sector?.name,
      profile?.sectorName,
      profile?.professionRoleTitle,
      profile?.professionRole?.title,
      profile?.sectorCode,
    ) || "Not set",
  },
  {
    label: "Education",
    value: titleCase(firstText(profile?.educationLevel, profile?.education?.[0]?.educationLevel) || "Not set"),
  },
  {
    label: "Visibility",
    value: profile?.isPrivate ? "Private" : "Visible",
  },
  {
    label: "Type",
    value: titleCase(firstText(profile?.profileType, profile?.type) || "Professional"),
  },
];

const profileCapturedItems = (profile, user) => {
  const profileSummary = profile?.profileSummary || [profile?.description, profile?.experience].filter(Boolean).join("\n\n");
  const photoIds = toList(profile?.photoFileIds).length ? profile.photoFileIds : toList(profile?.photos).filter((item) => !isUrlValue(item));
  const workExperience = firstList(
    profile?.workExperienceHistory,
    profile?.workExperience,
    profile?.workExperiences,
    profile?.experienceHistory,
    profile?.employmentHistory,
  );
  const certificationItems = firstList(profile?.certifications, profile?.certificates).filter((item) => !isLicenseCredential(item));
  const licenseItems = firstList(profile?.licenses, toList(profile?.certifications).filter(isLicenseCredential));
  const education = firstList(profile?.education, profile?.educationHistory);
  const documents = firstList(profile?.documents, profile?.documentFileIds);
  const references = firstList(profile?.references, profile?.professionalReferences);
  const item = (itemType, propertyPath, currentItemValue, required, sourceValue = currentItemValue, displayValue = currentItemValue, currentItem = structuredCurrentItem(sourceValue)) => ({
    itemType,
    propertyPath,
    currentItem,
    currentItemValue: displayValue || currentItemValue,
    required,
    sourceValue,
    displayValue,
  });
  const mediaItem = (itemType, propertyPath, sourceValue, label) => {
    const structured = structuredCurrentItem(sourceValue);
    return item(
      itemType,
      propertyPath,
      mediaDisplayValue(sourceValue, label),
      false,
      sourceValue,
      mediaDisplayValue(sourceValue, label),
      Array.isArray(structured) && !structured.length ? [] : structured,
    );
  };

  return [
    item("IDENTITY", "identity", identitySummary(user), true),
    item("ADDRESS", "address", addressSummary(user), true),
    item("CRIMINAL_RECORD", "criminalRecord", "Criminal record screening requested", true),
    item("SEX_OFFENDER_REGISTRY", "sexOffenderRegistry", "Sex offender registry screening requested", true),
    item("WATCHLIST", "watchlist", "Watchlist screening requested", true),
    item("PROFILE_TYPE", "profileType", compactValue(profile?.profileType), true),
    item("PROFILE_NAME", "profileName", compactValue(profile?.profileName || profileTitle(profile)), true),
    item("SECTOR", "sector", compactValue(profile?.sector || profile?.sectorCode || profile?.sectorCodes || profile?.sectors), true),
    item("PROFILE_SUMMARY", "profileSummary", profileSummary, true, profileSummary, richTextPlainText(profileSummary) || "Not provided"),
    item("INTERESTED_SERVICE", "interestedServices", compactValue(profile?.interestedServices), false),
    item("INTERESTED_LOCATION", "interestedLocations", compactValue(profile?.interestedLocations || profile?.previousRegionsWorkedIn), false),
    item("INTERESTED_SHIFT", "interestedShifts", compactValue(profile?.interestedShifts), false),
    item("WORK_EXPERIENCE", "workExperienceHistory", detailedListValue(workExperience), false, workExperience, detailedListValue(workExperience)),
    item("EDUCATION", "education", education.length ? detailedListValue(education) : compactValue(profile?.educationLevel), false, education.length ? education : profile?.educationLevel, education.length ? detailedListValue(education) : compactValue(profile?.educationLevel)),
    mediaItem("CERTIFICATION", "certifications", certificationItems, "Certification"),
    mediaItem("LICENSE", "licenses", licenseItems, "License"),
    mediaItem("DOCUMENT", "documents", documents, "Document"),
    mediaItem("PHOTO", "photos", toList(profile?.photos).length ? profile?.photos : photoIds, "Photo"),
    item("REFERENCE", "references", detailedListValue(references), false, references, detailedListValue(references)),
  ];
};

const previewFileRefs = (item) => {
  const type = String(item?.propertyPath || item?.itemType || "").toLowerCase();
  if (!/(photo|document|certification)/.test(type)) return [];
  const source = item?.sourceValue ?? item?.currentItemValue;
  const values = Array.isArray(source) ? source : toList(source).length ? toList(source) : [source];
  const refs = values.flatMap((value, index) => directFileRefs(value, `${titleCase(item.propertyPath)} ${index + 1}`));
  const seen = new Set();
  return refs.filter((ref) => {
    if (!ref.fileId || seen.has(ref.fileId)) return false;
    seen.add(ref.fileId);
    return true;
  });
};

const verificationItemForSubmit = ({ sourceValue, displayValue, ...item }) => item;

const profileReviewRows = (profile, user) =>
  profileCapturedItems(profile, user).map((item, index) => ({
    key: `${item.itemType}-${item.propertyPath}-${index}`,
    item,
    label: titleCase(item.propertyPath),
    value: item.displayValue ?? item.currentItemValue,
    fileRefs: previewFileRefs(item),
    required: item.required,
  }));

const splitReviewRows = (rows) => ({
  trust: rows.slice(0, 5),
  profile: rows.slice(5, 9),
  professional: rows.slice(9, 15),
  attachments: rows.slice(15),
});

const checkTitle = (check) =>
  titleCase(firstText(check?.checkType, check?.type, check?.verificationType) || "Background check");

const isTerminalStatus = (status) =>
  ["COMPLETED", "VERIFIED", "APPROVED", "PASSED", "FAILED", "REJECTED", "CANCELLED", "CANCELED"].includes(
    String(status || "").toUpperCase(),
  );

const isRequiredSubscriptionMissing = (error) =>
  /required\s+subscription\s+missing/i.test(String(error?.message || error || ""));

export default function BackgroundChecks() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedWorkspaceId } = useWorkspace();
  const actorId = user?.id || user?.userId || "";
  const [profiles, setProfiles] = useState([]);
  const [checksByProfile, setChecksByProfile] = useState({});
  const [expandedProfiles, setExpandedProfiles] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [processingKey, setProcessingKey] = useState("");
  const [selectedCheck, setSelectedCheck] = useState(null);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [previewProfile, setPreviewProfile] = useState(null);
  const [excludedPreviewKeys, setExcludedPreviewKeys] = useState({});
  const [mediaPreview, setMediaPreview] = useState(null);
  const [verificationDrafts, setVerificationDrafts] = useState({});

  const totalChecks = useMemo(
    () => Object.values(checksByProfile).reduce((sum, checks) => sum + checks.length, 0),
    [checksByProfile],
  );
  const previewRows = useMemo(
    () => (previewProfile ? profileReviewRows(previewProfile, user) : []),
    [previewProfile, user],
  );
  const previewGroups = useMemo(() => splitReviewRows(previewRows), [previewRows]);
  const includedPreviewRows = useMemo(
    () => previewRows.filter((row) => !excludedPreviewKeys[row.key]),
    [excludedPreviewKeys, previewRows],
  );
  const excludedPreviewCount = previewRows.length - includedPreviewRows.length;

  const loadChecksForProfile = useCallback(async (profile) => {
    const profileId = profile?.id || profile?.professionProfileId;
    if (!profileId) return [];
    const result = await olreadiaService.backgroundChecks.listForProfessionProfile(profileId, undefined, {
      actorId,
      userId: actorId,
    });
    return toList(result).sort((a, b) => {
      const left = new Date(b.requestedAt || b.createdAt || "").getTime() || 0;
      const right = new Date(a.requestedAt || a.createdAt || "").getTime() || 0;
      return left - right;
    });
  }, [actorId]);

  const loadPage = useCallback(async () => {
    if (!actorId) return;
    try {
      setLoading(true);
      setError("");
      const profileResult = await olreadiaService.professionProfiles.listForUser(actorId, {
        actorId,
        workspaceId: selectedWorkspaceId,
        page: 0,
        size: 100,
      });
      const nextProfiles = toList(profileResult);
      setProfiles(nextProfiles);
      const entries = await Promise.all(
        nextProfiles.map(async (profile) => {
          const profileId = profile?.id || profile?.professionProfileId;
          try {
            return [profileId, await loadChecksForProfile(profile)];
          } catch {
            return [profileId, []];
          }
        }),
      );
      setChecksByProfile(Object.fromEntries(entries.filter(([profileId]) => profileId)));
    } catch (nextError) {
      setError(nextError?.message || "Could not load background checks.");
      setProfiles([]);
      setChecksByProfile({});
    } finally {
      setLoading(false);
    }
  }, [actorId, loadChecksForProfile, selectedWorkspaceId]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  const requestCheck = async (profile) => {
    const profileId = profile?.id || profile?.professionProfileId;
    if (!profileId || !actorId || processingKey) return;
    setExcludedPreviewKeys({});
    setPreviewProfile(profile);
  };

  const closePreview = () => {
    if (processingKey) return;
    setPreviewProfile(null);
    setExcludedPreviewKeys({});
  };

  const togglePreviewItem = (key) => {
    setExcludedPreviewKeys((current) => ({ ...current, [key]: !current[key] }));
  };

  const submitPreviewRequest = async () => {
    const profile = previewProfile;
    const profileId = profile?.id || profile?.professionProfileId;
    if (!profileId || !actorId || processingKey) return;
    if (!includedPreviewRows.length) {
      message.warning("Please include at least one profile item for verification.");
      return;
    }
    const key = `request-${profileId}`;
    try {
      setProcessingKey(key);
      await olreadiaService.backgroundChecks.requestForProfessionProfile(
        profileId,
        {
          checkType: "PROFILE_VALIDATION",
          status: "REQUESTED",
          userId: actorId,
          verificationItems: includedPreviewRows.map((row) => verificationItemForSubmit(row.item)),
          notes: `Captured ${includedPreviewRows.length} profile values for ${profileTitle(profile)}`,
        },
        actorId,
      );
      message.success("Background check requested.");
      setPreviewProfile(null);
      setExcludedPreviewKeys({});
      setMediaPreview(null);
      const checks = await loadChecksForProfile(profile);
      setChecksByProfile((current) => ({ ...current, [profileId]: checks }));
      setExpandedProfiles((current) => ({ ...current, [profileId]: true }));
    } catch (nextError) {
      if (isRequiredSubscriptionMissing(nextError)) {
        Modal.confirm({
          title: "Subscription required",
          content: nextError?.message || "A subscription is required before requesting a background check.",
          okText: "Add Subscription",
          cancelText: "Cancel",
          onOk: () => navigate(selectedWorkspaceId ? paths.workspaceSubscriptions(selectedWorkspaceId) : paths.workspaces),
        });
      } else {
        message.error(nextError?.message || "Could not request background check.");
      }
    } finally {
      setProcessingKey("");
    }
  };

  const cancelCheck = async (check) => {
    if (!check?.id || processingKey) return;
    const key = `cancel-${check.id}`;
    try {
      setProcessingKey(key);
      await olreadiaService.backgroundChecks.cancel(check.id, actorId);
      message.success("Background check cancelled.");
      setSelectedCheck(null);
      await loadPage();
    } catch (nextError) {
      message.error(nextError?.message || "Could not cancel background check.");
    } finally {
      setProcessingKey("");
    }
  };

  const verifyCheckItem = async (item, status) => {
    if (!selectedCheck?.id || processingKey) return;
    const key = `${item?.itemType || ""}:${item?.propertyPath || ""}`;
    const processing = `verify-${selectedCheck.id}-${key}-${status}`;
    try {
      setProcessingKey(processing);
      const updated = await olreadiaService.backgroundChecks.submitItemResult(
        selectedCheck.id,
        {
          ...item,
          status,
          matchResult: status === "PASS" ? "TOTAL_MATCH" : "MISMATCH",
          valueFound: firstText(verificationDrafts[key], item?.valueFound),
        },
        actorId,
      );
      setSelectedCheck(updated);
      const profileId = updated?.professionProfileId || selectedProfile?.id || selectedProfile?.professionProfileId;
      if (profileId) {
        setChecksByProfile((current) => ({
          ...current,
          [profileId]: toList(current[profileId]).map((check) => (check.id === updated.id ? updated : check)),
        }));
      }
      message.success("Verification item updated.");
    } catch (nextError) {
      message.error(nextError?.message || "Could not update verification item.");
    } finally {
      setProcessingKey("");
    }
  };

  return (
    <div className="olreadia-flow-page">
      <div className="olreadia-flow-layout">
        <main className="olreadia-flow-main">
          <section className="olreadia-flow-hero">
            <span className="olreadia-flow-hero__icon"><SafetyCertificateOutlined /></span>
            <div>
              <Text className="olreadia-flow-eyebrow">Background checks</Text>
              <Title level={2}>Request Verification</Title>
              <Paragraph>
                Keep profile verification ready so hiring teams can move faster when an offer or start date depends on screening.
              </Paragraph>
            </div>
            <Button icon={<ReloadOutlined />} onClick={loadPage} loading={loading}>
              Refresh
            </Button>
          </section>

          <div className="olreadia-flow-toolbar">
            <Space wrap>
              <Tag className="olreadia-flow-status is-active">{profiles.length} profile{profiles.length === 1 ? "" : "s"}</Tag>
              <Tag className="olreadia-flow-status is-good">{totalChecks} check{totalChecks === 1 ? "" : "s"}</Tag>
            </Space>
          </div>

          {error ? (
            <Alert
              type="error"
              showIcon
              message="Unable to load background checks"
              description={error}
              action={<Button onClick={loadPage}>Retry</Button>}
              style={{ marginBottom: 12 }}
            />
          ) : null}

          {loading && !profiles.length ? (
            <Card className="olreadia-flow-card"><Skeleton active paragraph={{ rows: 4 }} /></Card>
          ) : profiles.length ? (
            <List
              className="olreadia-flow-list"
              dataSource={profiles}
              renderItem={(profile) => {
                const profileId = profile?.id || profile?.professionProfileId;
                const checks = checksByProfile[profileId] || [];
                const expanded = Boolean(expandedProfiles[profileId]);
                return (
                  <List.Item className="olreadia-flow-list-item">
                    <Card className="olreadia-flow-card">
                      <div className="olreadia-flow-card__top">
                        <span className="olreadia-flow-card__icon"><IdcardOutlined /></span>
                        <div>
                          <Title level={4}>{profileTitle(profile)}</Title>
                          <div className="olreadia-verification-profile-row">
                            {profileDetailItems(profile).map((item) => (
                              <span key={item.label}>
                                <Text>{item.label}</Text>
                                <strong>{item.value}</strong>
                              </span>
                            ))}
                          </div>
                        </div>
                        <Tag className={`olreadia-flow-status is-${checks.length ? "active" : "warn"}`}>
                          {checks.length ? `${checks.length} checks` : "No checks"}
                        </Tag>
                      </div>
                      <div className="olreadia-flow-actions">
                        <Button
                          className="olreadia-flow-button olreadia-flow-button--blue"
                          icon={<EyeOutlined />}
                          onClick={() => setSelectedProfile(profile)}
                        >
                          View Details
                        </Button>
                        <Button
                          type="primary"
                          className="olreadia-flow-button olreadia-flow-button--green"
                          icon={<SafetyCertificateOutlined />}
                          loading={processingKey === `request-${profileId}`}
                          disabled={Boolean(processingKey)}
                          onClick={() => requestCheck(profile)}
                        >
                          Request Verification
                        </Button>
                        <Button
                          className="olreadia-flow-button olreadia-flow-button--orange"
                          icon={<FileProtectOutlined />}
                          onClick={() => setExpandedProfiles((current) => ({ ...current, [profileId]: !expanded }))}
                        >
                          {expanded ? "Hide Checks" : "View Ongoing and Previous Checks"}
                        </Button>
                      </div>
                      {expanded ? (
                        <div className="olreadia-flow-nested-list">
                          {checks.length ? checks.map((check) => (
                            <button
                              type="button"
                              key={check.id || `${profileId}-${check.requestedAt}`}
                              className="olreadia-flow-nested-row"
                              onClick={() => {
                                setSelectedProfile(profile);
                                setSelectedCheck(check);
                              }}
                            >
                              <span><FileProtectOutlined /></span>
                              <span>
                                <strong>{checkTitle(check)}</strong>
                                <Text>Requested {formatDateTime(check.requestedAt || check.createdAt)}</Text>
                              </span>
                              <Tag className={`olreadia-flow-status is-${statusTone(check.status)}`}>
                                {titleCase(check.status || "Requested")}
                              </Tag>
                            </button>
                          )) : (
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No checks for this profile yet" />
                          )}
                        </div>
                      ) : null}
                    </Card>
                  </List.Item>
                );
              }}
            />
          ) : (
            <Card className="olreadia-flow-card">
              <Empty
                image={<IdcardOutlined style={{ fontSize: 44, color: "#0d8abc" }} />}
                description="No professional profiles found"
              >
                <Button type="primary" onClick={() => navigate(paths.professionalProfiles)}>
                  Create or review profile
                </Button>
              </Empty>
            </Card>
          )}
        </main>

        <SidePromotionSection className="olreadia-flow-promotions" />
      </div>

      <Drawer
        open={Boolean(previewProfile)}
        onClose={closePreview}
        width="min(760px, 100vw)"
        closable={false}
        className="olreadia-background-preview-drawer"
        footer={
          <div className="olreadia-background-preview__footer">
            <Button
              className="olreadia-flow-button olreadia-flow-button--neutral"
              disabled={Boolean(processingKey)}
              onClick={closePreview}
            >
              Review again
            </Button>
            <Button
              type="primary"
              className="olreadia-flow-button olreadia-flow-button--green"
              loading={processingKey === `request-${previewProfile?.id || previewProfile?.professionProfileId}`}
              disabled={Boolean(processingKey) || !includedPreviewRows.length}
              onClick={submitPreviewRequest}
            >
              Submit Verification Request
            </Button>
          </div>
        }
      >
        {previewProfile ? (
          <div className="olreadia-background-preview">
            <div className="olreadia-background-preview__hero">
              <span className="olreadia-background-preview__icon"><SafetyCertificateOutlined /></span>
              <div>
                <Text className="olreadia-flow-eyebrow">Verification preview</Text>
                <Title level={4}>{profileTitle(previewProfile)}</Title>
                <Text>Choose the profile data to include before submitting the background check request.</Text>
                <div className="olreadia-background-preview__header-actions">
                  <Button
                    size="small"
                    className="olreadia-flow-button olreadia-flow-button--blue"
                    icon={<EditOutlined />}
                    onClick={() => navigate(paths.professionalProfileEdit(previewProfile.id || previewProfile.professionProfileId))}
                  >
                    Edit Profile
                  </Button>
                  <Button
                    size="small"
                    className="olreadia-flow-button olreadia-flow-button--neutral"
                    icon={<IdcardOutlined />}
                    onClick={() => navigate(paths.profile)}
                  >
                    Edit User Data
                  </Button>
                </div>
              </div>
              <div className="olreadia-background-preview__summary">
                <strong>{includedPreviewRows.length}</strong>
                <Text>included</Text>
                {excludedPreviewCount ? <Tag className="olreadia-flow-status is-warn">{excludedPreviewCount} excluded</Tag> : null}
              </div>
              <Button
                type="text"
                className="olreadia-background-preview__close"
                icon={<CloseOutlined />}
                disabled={Boolean(processingKey)}
                onClick={closePreview}
              />
            </div>
            <PreviewGroup
              title="Trust Screening"
              rows={previewGroups.trust}
              excludedKeys={excludedPreviewKeys}
              onToggle={togglePreviewItem}
              onPreviewFile={setMediaPreview}
            />
            <PreviewGroup
              title="Profile Details"
              rows={previewGroups.profile}
              excludedKeys={excludedPreviewKeys}
              onToggle={togglePreviewItem}
              onPreviewFile={setMediaPreview}
            />
            <PreviewGroup
              title="Professional Details"
              rows={previewGroups.professional}
              excludedKeys={excludedPreviewKeys}
              onToggle={togglePreviewItem}
              onPreviewFile={setMediaPreview}
            />
            <PreviewGroup
              title="Files and References"
              rows={previewGroups.attachments}
              excludedKeys={excludedPreviewKeys}
              onToggle={togglePreviewItem}
              onPreviewFile={setMediaPreview}
            />
          </div>
        ) : null}
      </Drawer>

      <Modal
        open={Boolean(mediaPreview)}
        title={mediaPreview?.label || "Review file"}
        onCancel={() => setMediaPreview(null)}
        width="min(720px, 100vw)"
        footer={mediaPreview?.url ? [
          <Button key="open" type="primary" href={mediaPreview.url} target="_blank" rel="noreferrer">
            Open
          </Button>,
        ] : null}
      >
        {mediaPreview ? (
          <div className="olreadia-flow-media-preview">
            {mediaPreview.url && mediaPreview.image ? (
              <img src={mediaPreview.url} alt="" />
            ) : (
              <div className="olreadia-flow-media-preview__fallback">
                {mediaPreview.image ? <FileImageOutlined /> : <FileTextOutlined />}
              </div>
            )}
            <div className="olreadia-flow-media-preview__details">
              <div>
                <Text>File</Text>
                <strong>{mediaPreview.label}</strong>
              </div>
              <div>
                <Text>Reference</Text>
                <strong>{mediaPreview.fileId ? "Uploaded file attached" : "Profile file URL"}</strong>
              </div>
              {!mediaPreview.url ? (
                <Alert
                  type="info"
                  showIcon
                  message="Preview not available"
                  description="This file is attached to the profile, but no viewable URL is available in the profile data."
                />
              ) : null}
            </div>
          </div>
        ) : null}
      </Modal>

      <Drawer
        open={Boolean(selectedProfile)}
        onClose={() => setSelectedProfile(null)}
        title={selectedProfile ? profileTitle(selectedProfile) : "Professional profile details"}
        width="min(620px, 100vw)"
        className="olreadia-flow-drawer"
      >
        {selectedProfile ? (
          <Space direction="vertical" size={10} className="olreadia-flow-drawer__stack">
            <Space wrap>
              <Tag className="olreadia-flow-status is-active">{titleCase(firstText(selectedProfile.profileType, selectedProfile.type) || "Professional")}</Tag>
              <Tag className={`olreadia-flow-status is-${selectedProfile.isPrivate ? "warn" : "good"}`}>
                {selectedProfile.isPrivate ? "Private" : "Visible"}
              </Tag>
            </Space>
            <div className="olreadia-profile-detail-actions">
              <Button
                className="olreadia-flow-button olreadia-flow-button--blue"
                icon={<EyeOutlined />}
                onClick={() => navigate(paths.professionalProfileDetails(selectedProfile.id || selectedProfile.professionProfileId))}
              >
                Open Full Details
              </Button>
              <Button
                type="primary"
                className="olreadia-flow-button olreadia-flow-button--green"
                icon={<EditOutlined />}
                onClick={() => navigate(paths.professionalProfileEdit(selectedProfile.id || selectedProfile.professionProfileId))}
              >
                Edit Profile
              </Button>
            </div>
            <div className="olreadia-flow-detail-grid">
              {profileDetailItems(selectedProfile).map((item) => (
                <div key={item.label}><Text>{item.label}</Text><strong>{item.value}</strong></div>
              ))}
            </div>
            <ProfileDrawerSection
              title="Summary"
              value={selectedProfile.profileSummary || selectedProfile.description || selectedProfile.summary}
              richText
            />
            <ProfileDrawerSection title="Services" value={compactValue(selectedProfile.interestedServices)} />
            <ProfileDrawerSection title="Locations" value={compactValue(selectedProfile.interestedLocations || selectedProfile.previousRegionsWorkedIn)} />
            <ProfileDrawerSection
              title="Work Experience"
              value={detailedListValue(firstList(
                selectedProfile.workExperienceHistory,
                selectedProfile.workExperience,
                selectedProfile.workExperiences,
                selectedProfile.experienceHistory,
                selectedProfile.employmentHistory,
              ))}
            />
            <ProfileDrawerSection title="Education" value={detailedListValue(firstList(selectedProfile.education, selectedProfile.educationHistory))} />
            <ProfileFilePreviewSection
              title="Certifications"
              items={firstList(selectedProfile.certifications, selectedProfile.certificates).filter((item) => !isLicenseCredential(item))}
              fallback="Certification"
              onPreview={setMediaPreview}
            />
            <ProfileFilePreviewSection
              title="Licenses"
              items={firstList(selectedProfile.licenses, toList(selectedProfile.certifications).filter(isLicenseCredential))}
              fallback="License"
              onPreview={setMediaPreview}
            />
            <ProfileFilePreviewSection
              title="Documents"
              items={firstList(selectedProfile.documents, selectedProfile.documentFileIds)}
              fallback="Document"
              onPreview={setMediaPreview}
            />
            <ProfileFilePreviewSection
              title="Photos"
              items={toList(selectedProfile.photos).length ? selectedProfile.photos : selectedProfile.photoFileIds}
              fallback="Photo"
              onPreview={setMediaPreview}
            />
            <ProfileDrawerSection title="References" value={detailedListValue(firstList(selectedProfile.references, selectedProfile.professionalReferences))} />
            <Button
              className="olreadia-flow-button olreadia-flow-button--blue"
              icon={<EyeOutlined />}
              onClick={() => navigate(paths.professionalProfileDetails(selectedProfile.id || selectedProfile.professionProfileId))}
            >
              Open Full Details
            </Button>
            <Button
              type="primary"
              className="olreadia-flow-button olreadia-flow-button--green"
              icon={<EditOutlined />}
              onClick={() => navigate(paths.professionalProfileEdit(selectedProfile.id || selectedProfile.professionProfileId))}
            >
              Edit Profile
            </Button>
          </Space>
        ) : null}
      </Drawer>

      <Drawer
        open={Boolean(selectedCheck)}
        onClose={() => setSelectedCheck(null)}
        title={selectedCheck ? checkTitle(selectedCheck) : "Background check details"}
        width="min(480px, 100vw)"
        className="olreadia-flow-drawer"
      >
        {selectedCheck ? (
          <Space direction="vertical" size={10} className="olreadia-flow-drawer__stack">
            <Space wrap>
              <Tag className={`olreadia-flow-status is-${statusTone(selectedCheck.status)}`}>
                {titleCase(selectedCheck.status || "Requested")}
              </Tag>
              <Tag>{checkTitle(selectedCheck)}</Tag>
            </Space>
            <div className="olreadia-flow-detail-grid">
              <div><Text>Requested</Text><strong>{formatDateTime(selectedCheck.requestedAt || selectedCheck.createdAt)}</strong></div>
              <div><Text>Completed</Text><strong>{formatDateTime(selectedCheck.completedAt || selectedCheck.updatedAt)}</strong></div>
              <div><Text>Profile</Text><strong>{profileTitle(selectedCheck.professionProfile || selectedCheck.profile || {})}</strong></div>
              <div><Text>Requested by</Text><strong>{firstText(selectedCheck.requestedByName, selectedCheck.requestedByUserId) || "Not set"}</strong></div>
            </div>
            {selectedCheck.notes || selectedCheck.summary ? (
              isUrlValue(selectedCheck.notes || selectedCheck.summary) ? (
                <Button
                  className="olreadia-flow-button olreadia-flow-button--blue olreadia-flow-view-link"
                  size="small"
                  href={selectedCheck.notes || selectedCheck.summary}
                  target="_blank"
                  rel="noreferrer"
                >
                  View detail
                </Button>
              ) : (
                <Paragraph className="olreadia-flow-drawer__note">{selectedCheck.notes || selectedCheck.summary}</Paragraph>
              )
            ) : null}
            {toList(selectedCheck.verificationItems || selectedCheck.items).length ? (
              <div className="olreadia-flow-nested-list">
                {toList(selectedCheck.verificationItems || selectedCheck.items).map((item, index) => {
                  const itemKey = `${item?.itemType || ""}:${item?.propertyPath || ""}`;
                  const savingPass = processingKey === `verify-${selectedCheck.id}-${itemKey}-PASS`;
                  const savingFail = processingKey === `verify-${selectedCheck.id}-${itemKey}-FAIL`;
                  return (
                    <div key={item.id || itemKey || index} className="olreadia-flow-nested-row olreadia-flow-nested-row--compact olreadia-background-check-item">
                      <span><CheckCircleOutlined /></span>
                      <span>
                        <strong>{titleCase(item.propertyPath || item.itemType || item.type || "Verification item")}</strong>
                        {verificationItemViewUrl(item) ? (
                          <Button
                            className="olreadia-flow-button olreadia-flow-button--blue olreadia-flow-view-link"
                            size="small"
                            href={verificationItemViewUrl(item)}
                            target="_blank"
                            rel="noreferrer"
                          >
                            View
                          </Button>
                        ) : (
                          <Text>{verificationItemValue(item) || "Verification detail"}</Text>
                        )}
                        <Input.TextArea
                          className="olreadia-background-check-item__input"
                          size="small"
                          autoSize={{ minRows: 2, maxRows: 4 }}
                          placeholder="Capture data found"
                          value={verificationDrafts[itemKey] ?? item.valueFound ?? ""}
                          onChange={(event) => setVerificationDrafts((current) => ({ ...current, [itemKey]: event.target.value }))}
                        />
                        <Space wrap size={8}>
                          <Button
                            size="small"
                            className="olreadia-flow-button olreadia-flow-button--green"
                            loading={savingPass}
                            disabled={Boolean(processingKey)}
                            onClick={() => verifyCheckItem(item, "PASS")}
                          >
                            Pass
                          </Button>
                          <Button
                            size="small"
                            className="olreadia-flow-button olreadia-flow-button--danger"
                            loading={savingFail}
                            disabled={Boolean(processingKey)}
                            onClick={() => verifyCheckItem(item, "FAIL")}
                          >
                            Fail
                          </Button>
                        </Space>
                      </span>
                      {item.status || item.matchResult ? (
                        <Tag>{titleCase(item.status || item.matchResult)}</Tag>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ) : null}
            {!isTerminalStatus(selectedCheck.status) ? (
              <Button
                danger
                className="olreadia-flow-button olreadia-flow-button--danger"
                icon={<StopOutlined />}
                loading={processingKey === `cancel-${selectedCheck.id}`}
                disabled={Boolean(processingKey)}
                onClick={() => cancelCheck(selectedCheck)}
              >
                Cancel request
              </Button>
            ) : null}
          </Space>
        ) : null}
      </Drawer>
    </div>
  );
}

function PreviewGroup({ title, rows, excludedKeys, onToggle, onPreviewFile }) {
  if (!rows?.length) return null;
  return (
    <div className="olreadia-background-preview__group">
      <div className="olreadia-background-preview__group-title">{title}</div>
      <div className="olreadia-background-preview__rows">
        {rows.map((row) => (
          <div
            key={row.key}
            className={`olreadia-background-preview__row${excludedKeys?.[row.key] ? " is-excluded" : ""}`}
          >
            <span className="olreadia-background-preview__row-label">
              <Text>{row.label}</Text>
              {row.required ? <Tag className="olreadia-flow-status is-active">Core</Tag> : null}
            </span>
            <div className="olreadia-background-preview__value">
              <strong>{row.value}</strong>
              {row.fileRefs?.length ? (
                <div className="olreadia-background-preview__files">
                  {row.fileRefs.map((file) => (
                    <button
                      key={`${row.key}-${file.fileId}`}
                      type="button"
                      className="olreadia-background-preview__file-tile"
                      disabled={!file.url}
                      onClick={() => onPreviewFile(file)}
                    >
                      <span className="olreadia-background-preview__file-thumb">
                        {file.url && file.image ? <img src={file.url} alt="" /> : <FileTextOutlined />}
                      </span>
                      <span>
                        <strong>{file.label}</strong>
                        <Text>{file.url ? "View" : "No preview URL"}</Text>
                      </span>
                      {file.url ? <EyeOutlined /> : null}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
            <span className="olreadia-background-preview__toggle">
              <Text>{excludedKeys?.[row.key] ? "Excluded" : "Included"}</Text>
              <Switch
                size="small"
                checked={!excludedKeys?.[row.key]}
                onChange={() => onToggle(row.key)}
              />
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProfileDrawerSection({ title, value, richText = false }) {
  const displayValue = value || "Not provided";
  if (richText && !hasRichText(value)) return null;
  if (!richText && displayValue === "Not provided") return null;
  return (
    <div className="olreadia-profile-detail-section">
      <Text>{title}</Text>
      {richText ? (
        <div
          className="olreadia-rich-text olreadia-profile-detail-section__rich"
          dangerouslySetInnerHTML={richTextMarkup(value)}
        />
      ) : (
        <strong>{displayValue}</strong>
      )}
    </div>
  );
}

function ProfileFilePreviewSection({ title, items, fallback, onPreview }) {
  const list = Array.isArray(items) ? items : toList(items);
  const refs = list.flatMap((item, index) => directFileRefs(item, `${fallback} ${index + 1}`));
  if (!refs.length) return null;

  return (
    <div className="olreadia-profile-detail-section">
      <Text>{title}</Text>
      <div className="olreadia-profile-file-preview-grid">
        {refs.map((file) => (
          <button
            key={`${title}-${file.fileId || file.url}`}
            type="button"
            className="olreadia-background-preview__file-tile"
            disabled={!file.url}
            onClick={() => onPreview(file)}
          >
            <span className="olreadia-background-preview__file-thumb">
              {file.url && file.image ? <img src={file.url} alt="" /> : <FileTextOutlined />}
            </span>
            <span>
              <strong>{file.label}</strong>
              <Text>{file.url ? "Preview" : "No preview URL"}</Text>
            </span>
            {file.url ? <EyeOutlined /> : null}
          </button>
        ))}
      </div>
    </div>
  );
}
